import { BadgeStatus } from "../BadgeStatus";
import { IoDocumentTextOutline, IoCalendarOutline } from "react-icons/io5";
import { FiClock } from "react-icons/fi";
import { IoIosArrowForward } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import type { Ocurrence } from "@/types/Ocurrence";
import { getTimeAgo } from "@/utils/getTimeAgo";
import { formatDateTime } from "@/utils/formatDateTime";
import { convertStatusToLabel } from "@/utils/convertStatusToLabel";
import { getStatusColor } from "@/utils/getStatusColor";

type OcurrenceItemProps = {
  data: Ocurrence;
};

export const OcurrenceItem = (props: OcurrenceItemProps) => {
  const navigator = useNavigate();

  return (
    <div
      onClick={() =>
        navigator(`/ocurrences/details/${props.data.id}`, {
          state: { occurrence: props.data },
        })
      }
      className="w-full flex flex-col bg-white rounded-xl md:rounded-2xl shadow-sm cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group active:scale-[0.99] overflow-hidden"
    >
      <div className="p-4 md:p-5 flex gap-3 md:gap-4">
        <div className="relative h-20 w-20 md:h-24 md:w-24 flex-shrink-0 overflow-hidden rounded-xl md:rounded-2xl bg-gradient-to-br from-gray-100 to-gray-50">
          {props.data.media[0]?.type === "video" ? (
            <video
              src={(import.meta.env.VITE_BUCKET_URL || '') + "/" + props.data.media[0]?.key + "#t=0.1"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              preload="metadata"
              playsInline
              muted
            />
          ) : (
            <img
              src={(import.meta.env.VITE_BUCKET_URL || '') + "/" + props.data.media[0]?.key}
              alt="Ocorrência"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        
        <div className="flex flex-1 flex-col gap-2 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <IoDocumentTextOutline className="text-sm text-gray-400 flex-shrink-0" />
                <span className="text-xs font-semibold text-gray-600">
                  Protocolo: {props.data.protocol}
                </span>
              </div>
              <h3 className="font-bold text-sm md:text-base text-gray-800 line-clamp-1 group-hover:text-primary transition-colors">
                {props.data.category.name}
              </h3>
              <p className="text-xs text-gray-500 line-clamp-1">
                {props.data.subcategory.name}
              </p>
            </div>
            <IoIosArrowForward className="text-lg md:text-xl flex-shrink-0 text-gray-400 group-hover:text-primary group-hover:translate-x-0.5 transition-all mt-1" />
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-600">
            <IoCalendarOutline className="text-sm text-gray-400 flex-shrink-0" />
            <span className="font-medium">{formatDateTime(props.data.createdAt)}</span>
            <span className="text-gray-400">•</span>
            <FiClock className="text-sm text-gray-400 flex-shrink-0" />
            <span className="font-medium">{getTimeAgo(props.data.createdAt)}</span>
          </div>

          <div className="flex gap-2 flex-wrap">
            <BadgeStatus
              title={convertStatusToLabel(props.data.status)}
              color={getStatusColor(props.data.status)}
            />
            <BadgeStatus
              title={
                props.data.userPerceivedUrgency === "LOW"
                  ? "Baixa"
                  : props.data.userPerceivedUrgency === "MEDIUM"
                  ? "Média"
                  : props.data.userPerceivedUrgency === "URGENT"
                  ? "Urgente"
                  : "Alta"
              }
              color={
                props.data.userPerceivedUrgency === "LOW"
                  ? "blue"
                  : props.data.userPerceivedUrgency === "MEDIUM"
                  ? "yellow"
                  : "red"
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};
